/**
 * Хук для анимаций Git-графа
 * Управляет анимациями появления, перемещения и изменения узлов
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { GraphState, CommitNodeVisual, NodePosition } from './types';

/** Состояние анимации для узла */
interface NodeAnimationState {
  isNew: boolean;
  isAnimating: boolean;
  previousPosition?: NodePosition;
}

/** Состояние анимации для графа */
interface GraphAnimationState {
  nodes: Map<string, NodeAnimationState>;
  edges: Map<string, boolean>; // isNew
  isAnimating: boolean;
}

/** Параметры хука */
interface UseGraphAnimationOptions {
  /** Длительность анимации появления (мс) */
  appearDuration?: number;
  /** Длительность анимации перемещения (мс) */
  moveDuration?: number;
  /** Задержка между анимациями узлов (мс) */
  staggerDelay?: number;
  /** Включить анимации */
  enabled?: boolean;
}

/** Результат хука */
interface UseGraphAnimationResult {
  /** Состояние анимации для каждого узла */
  nodeStates: Map<string, NodeAnimationState>;
  /** Нужно ли анимировать ребро */
  isEdgeNew: (fromSha: string, toSha: string) => boolean;
  /** Общее состояние анимации */
  isAnimating: boolean;
  /** Сбросить состояние анимации */
  resetAnimation: () => void;
  /** Запустить анимацию для конкретного узла */
  animateNode: (sha: string) => void;
}

/**
 * Хук для управления анимациями Git-графа
 */
export function useGraphAnimation(
  graphState: GraphState,
  options: UseGraphAnimationOptions = {}
): UseGraphAnimationResult {
  const { appearDuration = 500, moveDuration = 300, staggerDelay = 50, enabled = true } = options;

  // Предыдущее состояние графа для сравнения
  const prevGraphRef = useRef<GraphState | null>(null);

  // Состояние анимаций
  const [animationState, setAnimationState] = useState<GraphAnimationState>(() => ({
    nodes: new Map(),
    edges: new Map(),
    isAnimating: false,
  }));

  // Отслеживаем изменения графа
  useEffect(() => {
    if (!enabled) {
      prevGraphRef.current = graphState;
      return;
    }

    const prevGraph = prevGraphRef.current;
    const newNodes = new Map<string, NodeAnimationState>();
    const newEdges = new Map<string, boolean>();
    let hasChanges = false;

    // Проверяем изменения в узлах
    graphState.nodes.forEach((node, sha) => {
      const prevNode = prevGraph?.nodes.get(sha);

      if (!prevNode) {
        // Новый узел - анимация появления
        newNodes.set(sha, {
          isNew: true,
          isAnimating: true,
        });
        hasChanges = true;
      } else if (
        prevNode.position.x !== node.position.x ||
        prevNode.position.y !== node.position.y
      ) {
        // Перемещение узла
        newNodes.set(sha, {
          isNew: false,
          isAnimating: true,
          previousPosition: prevNode.position,
        });
        hasChanges = true;
      } else {
        // Без изменений
        newNodes.set(sha, {
          isNew: false,
          isAnimating: false,
        });
      }
    });

    // Проверяем новые рёбра
    graphState.edges.forEach((edge) => {
      const edgeKey = `${edge.fromSha}-${edge.toSha}`;
      const prevEdge = prevGraph?.edges.find(
        (e) => e.fromSha === edge.fromSha && e.toSha === edge.toSha
      );

      if (!prevEdge) {
        newEdges.set(edgeKey, true);
        hasChanges = true;
      } else {
        newEdges.set(edgeKey, false);
      }
    });

    // Обновляем состояние если есть изменения
    if (hasChanges) {
      setAnimationState({
        nodes: newNodes,
        edges: newEdges,
        isAnimating: true,
      });

      // Сбрасываем анимацию после завершения
      const maxDuration =
        Math.max(appearDuration, moveDuration) + graphState.nodes.size * staggerDelay;

      const timer = setTimeout(() => {
        setAnimationState((prev) => ({
          ...prev,
          isAnimating: false,
          nodes: new Map(
            Array.from(prev.nodes.entries()).map(([sha, state]) => [
              sha,
              { ...state, isNew: false, isAnimating: false },
            ])
          ),
        }));
      }, maxDuration);

      prevGraphRef.current = graphState;
      return () => clearTimeout(timer);
    }

    prevGraphRef.current = graphState;
  }, [graphState, enabled, appearDuration, moveDuration, staggerDelay]);

  // Проверка isNew для ребра
  const isEdgeNew = useCallback(
    (fromSha: string, toSha: string): boolean => {
      const edgeKey = `${fromSha}-${toSha}`;
      return animationState.edges.get(edgeKey) ?? false;
    },
    [animationState.edges]
  );

  // Сброс анимации
  const resetAnimation = useCallback(() => {
    setAnimationState({
      nodes: new Map(),
      edges: new Map(),
      isAnimating: false,
    });
    prevGraphRef.current = null;
  }, []);

  // Анимация конкретного узла
  const animateNode = useCallback(
    (sha: string) => {
      setAnimationState((prev) => {
        const newNodes = new Map(prev.nodes);
        newNodes.set(sha, {
          isNew: true,
          isAnimating: true,
        });
        return {
          ...prev,
          nodes: newNodes,
          isAnimating: true,
        };
      });

      // Сбрасываем после анимации
      setTimeout(() => {
        setAnimationState((prev) => {
          const newNodes = new Map(prev.nodes);
          const currentState = newNodes.get(sha);
          if (currentState) {
            newNodes.set(sha, {
              ...currentState,
              isNew: false,
              isAnimating: false,
            });
          }
          return {
            ...prev,
            nodes: newNodes,
            isAnimating: Array.from(newNodes.values()).some((s) => s.isAnimating),
          };
        });
      }, appearDuration);
    },
    [appearDuration]
  );

  return {
    nodeStates: animationState.nodes,
    isEdgeNew,
    isAnimating: animationState.isAnimating,
    resetAnimation,
    animateNode,
  };
}

/**
 * Хук для анимации появления новых коммитов
 */
export function useCommitAppearAnimation(
  commits: Map<string, CommitNodeVisual>,
  enabled: boolean = true
): Map<string, boolean> {
  const [appearingCommits, setAppearingCommits] = useState<Set<string>>(new Set());
  const prevCommitsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled) {
      prevCommitsRef.current = new Set(commits.keys());
      return;
    }

    const currentShas = new Set(commits.keys());
    const newShas = new Set<string>();

    // Находим новые коммиты
    currentShas.forEach((sha) => {
      if (!prevCommitsRef.current.has(sha)) {
        newShas.add(sha);
      }
    });

    if (newShas.size > 0) {
      setAppearingCommits(newShas);

      // Убираем из appearing после анимации
      const timer = setTimeout(() => {
        setAppearingCommits(new Set());
      }, 500);

      prevCommitsRef.current = currentShas;
      return () => clearTimeout(timer);
    }

    prevCommitsRef.current = currentShas;
  }, [commits, enabled]);

  // Возвращаем Map для удобного доступа
  const result = new Map<string, boolean>();
  commits.forEach((_, sha) => {
    result.set(sha, appearingCommits.has(sha));
  });
  return result;
}

/**
 * Хук для анимации перемещения HEAD
 */
export function useHeadMoveAnimation(
  headPosition: NodePosition | null,
  enabled: boolean = true
): {
  currentPosition: NodePosition | null;
  previousPosition: NodePosition | null;
  isMoving: boolean;
} {
  const [isMoving, setIsMoving] = useState(false);
  const prevPositionRef = useRef<NodePosition | null>(null);
  const [previousPosition, setPreviousPosition] = useState<NodePosition | null>(null);

  useEffect(() => {
    if (!enabled || !headPosition) {
      prevPositionRef.current = headPosition;
      return;
    }

    const prevPos = prevPositionRef.current;

    if (prevPos && (prevPos.x !== headPosition.x || prevPos.y !== headPosition.y)) {
      // HEAD переместился
      setPreviousPosition(prevPos);
      setIsMoving(true);

      const timer = setTimeout(() => {
        setIsMoving(false);
        setPreviousPosition(null);
      }, 300);

      prevPositionRef.current = headPosition;
      return () => clearTimeout(timer);
    }

    prevPositionRef.current = headPosition;
  }, [headPosition, enabled]);

  return {
    currentPosition: headPosition,
    previousPosition,
    isMoving,
  };
}
